import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  Link,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import adminApi from "../../connection/adminApi";
import AdminLayout from "./AdminLayout";
import StoreLinkButton from "./StoreLinkButton";
import LoginAsButton from "./LoginAsButton";
import PurgeStoreButton from "./PurgeStoreButton";
import VectorStoreStatus, { StatusDot } from "./VectorStoreStatus";
import { readCachedVectorState } from "./vectorStatusCache";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const STATUSES = ["active", "trialing", "past_due", "canceled", "incomplete"];

const AdminStore = () => {
  const { id } = useParams();
  const toast = useToast();

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  // Last known state only; the live check runs when Store data is opened.
  const [vectorState, setVectorState] = useState(
    () => readCachedVectorState(id)?.state || null
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [payments, setPayments] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const [form, setForm] = useState({
    planCode: "",
    region: "",
    interval: "month",
    status: "active",
    trialEndsAt: "",
  });

  const loadStore = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await adminApi.get(`api/admin/stores/${id}`);
      setStore(data);
      setForm({
        planCode: data.subscription.planCode || "",
        region: data.subscription.region || "",
        interval: data.subscription.interval || "month",
        status: data.subscription.status || "active",
        trialEndsAt: data.subscription.currentPeriodEnd
          ? new Date(data.subscription.currentPeriodEnd)
              .toISOString()
              .slice(0, 10)
          : "",
      });
    } catch (error) {
      toast({
        status: "error",
        title: "Could not load the store",
        description: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);

    try {
      const { data } = await adminApi.get(`api/admin/stores/${id}/payments`);
      setPayments(data);
    } catch (error) {
      setPayments({ payments: [], error: true });
    } finally {
      setPaymentsLoading(false);
    }
  }, [id]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const { data } = await adminApi.get(`api/admin/stores/${id}/stats`);
      setStats(data);
    } catch (error) {
      setStats({ available: false });
    } finally {
      setStatsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadStore();
    loadPayments();
  }, [loadStore, loadPayments]);

  const selectedPlan = useMemo(
    () => (store?.plans || []).find((plan) => plan.code === form.planCode),
    [store, form.planCode]
  );

  // Only the regions the chosen plan is actually priced in can be selected, so
  // an operator cannot park a store on a plan with no price to charge.
  const availableRegions = useMemo(() => {
    if (!store) {
      return [];
    }

    if (!selectedPlan || !selectedPlan.prices.length) {
      return store.regions;
    }

    const priced = new Set(selectedPlan.prices.map((price) => price.region));

    return store.regions.filter((region) => priced.has(region.region));
  }, [store, selectedPlan]);

  const priceLabel = useMemo(() => {
    if (!selectedPlan) {
      return null;
    }

    const price = selectedPlan.prices.find(
      (candidate) =>
        candidate.region === form.region && candidate.interval === form.interval
    );

    if (!price) {
      return selectedPlan.isSelfServe ? "Free / no price" : "Quoted";
    }

    return `${price.amount.toLocaleString()} ${price.currency} / ${form.interval}`;
  }, [selectedPlan, form.region, form.interval]);

  const save = async () => {
    setSaving(true);

    const payload = {
      planCode: form.planCode,
      region: form.region,
      interval: form.interval,
      status: form.status,
    };

    // The date only means something for a trial: it is the day the merchant is
    // expected to start paying.
    if (form.status === "trialing" && form.trialEndsAt) {
      payload.trialEndsAt = new Date(`${form.trialEndsAt}T00:00:00Z`).toISOString();
    }

    try {
      const { data } = await adminApi.put(
        `api/admin/stores/${id}/subscription`,
        payload
      );

      const stripeNote = {
        "price-updated": "Stripe subscription moved to the new price.",
        "cancelled-at-period-end": "Stripe subscription cancelled at period end.",
        "no-live-subscription": "No Stripe subscription to update.",
        "skipped-by-operator": "Stripe was not touched.",
      }[data.stripe?.reason];

      toast({
        status: "success",
        duration: 6000,
        title: `Moved to ${data.planCode}`,
        description: `${data.status} · ${data.region} · ${data.currency || "—"}${
          stripeNote ? ` — ${stripeNote}` : ""
        }`,
      });

      await loadStore();
      await loadPayments();
    } catch (error) {
      toast({
        status: "error",
        duration: 12000,
        isClosable: true,
        title: "Could not change the subscription",
        description:
          error?.response?.data?.message ||
          "Nothing was changed on either side.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !store) {
    return (
      <AdminLayout>
        <Skeleton height="120px" borderRadius="lg" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Button
          as={RouterLink}
          to="/admin"
          size="sm"
          variant="ghost"
          leftIcon={<ArrowBackIcon />}
        >
          All stores
        </Button>

        <Box>
          <Heading size="md">{store.username}</Heading>
          <Text fontSize="sm" color="gray.500">
            {store.name} · {store.email}
          </Text>
        </Box>

        <Box flex="1" />
        <LoginAsButton storeId={id} />
        <StoreLinkButton store={store} />
      </Flex>

      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        <Tabs
          colorScheme="blue"
          index={tabIndex}
          onChange={(index) => {
            setTabIndex(index);
            if (index === 1 && !stats) loadStats();
          }}
        >
          <TabList>
            <Tab>Subscription</Tab>
            <Tab>
              <HStack spacing={2}>
                <span>Store data</span>
                {vectorState && <StatusDot state={vectorState} size="8px" />}
              </HStack>
            </Tab>
            <Tab color="red.600">Danger zone</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
                <Stat>
                  <StatLabel>Current plan</StatLabel>
                  <StatNumber fontSize="lg">
                    {store.subscription.planNameEn || "—"}
                  </StatNumber>
                  <StatHelpText>
                    <Badge
                      colorScheme={
                        store.subscription.status === "trialing"
                          ? "red"
                          : store.subscription.status === "active"
                          ? "green"
                          : "orange"
                      }
                    >
                      {store.subscription.status}
                    </Badge>
                    {store.subscription.status === "trialing" ? (
                      <Text as="span" ml={2} fontSize="xs" color="red.600">
                        all features open
                      </Text>
                    ) : null}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>Billing region</StatLabel>
                  <StatNumber fontSize="lg">
                    {(store.subscription.region || "—").toUpperCase()}
                  </StatNumber>
                  <StatHelpText>{store.subscription.currency || "—"}</StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>
                    {store.subscription.status === "trialing"
                      ? "Trial ends"
                      : "Renews"}
                  </StatLabel>
                  <StatNumber fontSize="lg">
                    {store.subscription.currentPeriodEnd
                      ? formatDate(store.subscription.currentPeriodEnd)
                      : "—"}
                  </StatNumber>
                  <StatHelpText>
                    {store.subscription.interval}
                    {store.subscription.cancelAtPeriodEnd ? " · cancelling" : ""}
                  </StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>Stripe customer</StatLabel>
                  <StatNumber fontSize="sm" fontFamily="mono">
                    {store.subscription.stripeCustomerId ? (
                      <Link
                        href={`https://dashboard.stripe.com/customers/${store.subscription.stripeCustomerId}`}
                        isExternal
                        color="blue.600"
                      >
                        {store.subscription.stripeCustomerId}
                        <ExternalLinkIcon mx="4px" mb="2px" />
                      </Link>
                    ) : (
                      "none"
                    )}
                  </StatNumber>
                  <StatHelpText>
                    {store.subscription.stripeSubscriptionId ? (
                      <Link
                        href={`https://dashboard.stripe.com/subscriptions/${store.subscription.stripeSubscriptionId}`}
                        isExternal
                        color="blue.600"
                      >
                        subscription <ExternalLinkIcon mx="2px" mb="2px" />
                      </Link>
                    ) : (
                      "no paid subscription"
                    )}
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider mb={4} />

              <Heading size="sm" mb={3}>
                Change subscription
              </Heading>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
                gap={3}
                mb={3}
              >
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Plan
                  </Text>
                  <Select
                    size="sm"
                    value={form.planCode}
                    onChange={(event) =>
                      setForm({ ...form, planCode: event.target.value })
                    }
                  >
                    {store.plans.map((plan) => (
                      <option key={plan.code} value={plan.code}>
                        {plan.nameEn}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Region / currency
                  </Text>
                  <Select
                    size="sm"
                    value={form.region}
                    onChange={(event) =>
                      setForm({ ...form, region: event.target.value })
                    }
                  >
                    {availableRegions.map((region) => (
                      <option key={region.region} value={region.region}>
                        {region.region.toUpperCase()} — {region.currency}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Billing period
                  </Text>
                  <Select
                    size="sm"
                    value={form.interval}
                    onChange={(event) =>
                      setForm({ ...form, interval: event.target.value })
                    }
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Status
                  </Text>
                  <Select
                    size="sm"
                    value={form.status}
                    onChange={(event) =>
                      setForm({ ...form, status: event.target.value })
                    }
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Grid>

              {form.status === "trialing" ? (
                <Box mb={3} maxW="320px">
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Trial ends / first payment due
                  </Text>
                  <Input
                    size="sm"
                    type="date"
                    value={form.trialEndsAt}
                    onChange={(event) =>
                      setForm({ ...form, trialEndsAt: event.target.value })
                    }
                  />
                  <Text fontSize="xs" color="red.600" mt={1}>
                    Every feature is unlocked until this date, and the merchant
                    sees a red trial badge counting down to it.
                  </Text>
                </Box>
              ) : null}

              <HStack mb={6}>
                <Button size="sm" onClick={save} isLoading={saving}>
                  Apply change
                </Button>
                <Text fontSize="sm" color="gray.500">
                  {priceLabel}
                </Text>
              </HStack>

              <Box
                bg="orange.50"
                color="orange.800"
                fontSize="xs"
                p={3}
                borderRadius="md"
                mb={6}
              >
                This grants the plan inside StorkPlus only. It does not create or
                cancel anything in Stripe, so use it for quoted deals, trials and
                support fixes — not to refund a paid subscription.
              </Box>

              <Divider mb={4} />

              <Flex align="center" mb={3} gap={3}>
                <Heading size="sm">Payments</Heading>
                {paymentsLoading ? <Spinner size="sm" /> : null}
                <Box flex="1" />
                <Button size="xs" variant="ghost" onClick={loadPayments}>
                  Refresh
                </Button>
              </Flex>

              {payments && payments.payments.length ? (
                <TableContainer>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Invoice</Th>
                        <Th>Date</Th>
                        <Th>Description</Th>
                        <Th isNumeric>Paid</Th>
                        <Th>Status</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {payments.payments.map((payment) => (
                        <Tr key={payment.id}>
                          <Td fontFamily="mono" fontSize="xs">
                            {payment.number || payment.id}
                          </Td>
                          <Td fontSize="sm">{formatDate(payment.created)}</Td>
                          <Td fontSize="sm">{payment.description || "—"}</Td>
                          <Td isNumeric fontSize="sm">
                            {payment.amountPaid.toLocaleString()} {payment.currency}
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                payment.status === "paid" ? "green" : "yellow"
                              }
                            >
                              {payment.status}
                            </Badge>
                          </Td>
                          <Td>
                            {payment.hostedInvoiceUrl ? (
                              <Link
                                href={payment.hostedInvoiceUrl}
                                isExternal
                                fontSize="xs"
                                color="blue.600"
                              >
                                Open <ExternalLinkIcon mx="2px" />
                              </Link>
                            ) : null}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  {paymentsLoading
                    ? "Loading…"
                    : payments?.stripeCustomerId
                    ? "No invoice yet — this store has never been charged."
                    : "No Stripe customer on file."}
                </Text>
              )}
            </TabPanel>

            <TabPanel px={0}>
              {tabIndex === 1 && (
                <VectorStoreStatus storeId={id} onState={setVectorState} />
              )}
              {statsLoading && !stats ? (
                <Skeleton height="100px" />
              ) : stats && stats.available ? (
                <>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                    <Stat>
                      <StatLabel>Products</StatLabel>
                      <StatNumber>{stats.stats.products}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Categories</StatLabel>
                      <StatNumber>{stats.stats.categories}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Customers</StatLabel>
                      <StatNumber>{stats.stats.customers}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Orders</StatLabel>
                      <StatNumber>{stats.stats.orders}</StatNumber>
                      <StatHelpText>{stats.stats.carts} open carts</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Admin users</StatLabel>
                      <StatNumber>{stats.stats.adminUsers}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Order value</StatLabel>
                      <StatNumber fontSize="lg">
                        {(stats.stats.revenueMinor / 100).toLocaleString()}{" "}
                        {stats.stats.currency || ""}
                      </StatNumber>
                      <StatHelpText>placed orders</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Channels</StatLabel>
                      <StatNumber>{store.channels.length}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Database</StatLabel>
                      <StatNumber fontSize="sm" fontFamily="mono">
                        {stats.databaseName}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <Heading size="sm" mb={2}>
                    Admin accounts
                  </Heading>
                  <TableContainer mb={6}>
                    <Table size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Username</Th>
                          <Th>Email</Th>
                          <Th>Last login</Th>
                          <Th>Enabled</Th>
                          <Th />
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.adminUsers.map((user) => (
                          <Tr key={user.id}>
                            <Td>{user.username}</Td>
                            <Td fontSize="sm">{user.email}</Td>
                            <Td fontSize="sm">{formatDate(user.lastLogin)}</Td>
                            <Td>
                              <Badge colorScheme={user.enabled ? "green" : "red"}>
                                {user.enabled ? "yes" : "no"}
                              </Badge>
                            </Td>
                            <Td textAlign="right">
                              {user.enabled && (
                                <LoginAsButton
                                  storeId={id}
                                  adminUserId={user.id}
                                  label="Log in as"
                                  variant="ghost"
                                />
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="sm" mb={2}>
                    Channels
                  </Heading>
                  <TableContainer>
                    <Table size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Name</Th>
                          <Th>Code</Th>
                          <Th>Hostname</Th>
                          <Th>Enabled</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {store.channels.map((channel) => (
                          <Tr key={channel.id}>
                            <Td>{channel.name}</Td>
                            <Td fontFamily="mono" fontSize="xs">
                              {channel.code}
                            </Td>
                            <Td fontSize="sm">
                              {channel.hostname ? (
                                <Link
                                  href={`https://${channel.hostname}`}
                                  isExternal
                                  color="blue.600"
                                >
                                  {channel.hostname} <ExternalLinkIcon mx="2px" />
                                </Link>
                              ) : (
                                "—"
                              )}
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={channel.enabled ? "green" : "gray"}
                              >
                                {channel.enabled ? "yes" : "no"}
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Text fontSize="sm" color="red.600">
                  The tenant database could not be read. It may never have been
                  provisioned.
                </Text>
              )}
            </TabPanel>

            <TabPanel px={0}>
              <Heading size="sm" mb={2} color="red.600">
                Purge this store
              </Heading>
              <Text fontSize="sm" color="gray.600" maxW="2xl" mb={4}>
                Drops the <Text as="span" fontFamily="mono">{store.databaseName}</Text>{" "}
                database, deletes every registry row for {store.username},
                clears the Redis cache, removes the uploaded files and deletes
                the Stripe customer. Nothing about this can be undone — it is
                here to clear test accounts, not to offboard a merchant.
              </Text>
              <PurgeStoreButton store={store} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </AdminLayout>
  );
};

export default AdminStore;
